import { useState, useEffect } from 'react';

export default function ConsolidationPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ period: '', parent_entity_id: '', description: '' });

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/entities/consolidation-runs');
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load consolidation runs');
      setRuns(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.period || !form.parent_entity_id) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/entities/consolidation-runs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period: form.period,
          parent_entity_id: form.parent_entity_id,
          description: form.description,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to create run');
      setForm({ period: '', parent_entity_id: '', description: '' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  async function calculate() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/entities/consolidation-runs/${selected}/calculate`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Calculate failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function post() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/entities/consolidation-runs/${selected}/post`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Post failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="entities-consolidation-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Consolidation</h1>
          <p className="muted">Run group consolidations, apply eliminations, and post consolidated results.</p>
        </div>
        <div className="actions">
          <button className="btn ghost" data-testid="entity-consol-add" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'New Run'}
          </button>
          <button className="btn secondary" data-testid="consol-run-calculate" disabled={!selected || busy} onClick={calculate}>
            Calculate
          </button>
          <button className="btn primary" data-testid="consol-run-post" disabled={!selected || busy} onClick={post}>
            Post
          </button>
        </div>
      </header>

      {error && <div className="alert error" data-testid="entities-consolidation-error">{error}</div>}

      {!selected && runs.length > 0 && (
        <div className="alert info">Select a run below to enable Calculate / Post.</div>
      )}

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>Period (YYYY-MM)
            <input value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} placeholder="e.g. 2026-06" />
          </label>
          <label>Parent entity ID
            <input value={form.parent_entity_id} onChange={(e) => setForm((f) => ({ ...f, parent_entity_id: e.target.value }))} placeholder="entity id" />
          </label>
          <label>Description
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="e.g. Q2 2026 Group Consol" />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.period || !form.parent_entity_id}>Create</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="entity-consol-list">
          <thead>
            <tr><th></th><th>Period</th><th>Parent entity</th><th>Description</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.id}
                data-testid={`entity-consol-row-${run.id}`}
                className={selected === run.id ? 'row-selected' : ''}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelected(run.id === selected ? null : run.id)}
              >
                <td><input type="radio" readOnly checked={selected === run.id} /></td>
                <td>{run.period}</td>
                <td>{run.parent_entity_id}</td>
                <td>{run.description || '—'}</td>
                <td><span className={`badge ${run.status === 'posted' ? 'success' : run.status === 'calculated' ? 'info' : ''}`}>{run.status || 'draft'}</span></td>
                <td>{run.created_at ? new Date(run.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
            {!loading && runs.length === 0 && (
              <tr><td colSpan={6} className="muted">No consolidation runs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
